import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import { listProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { SlidersHorizontal, ChevronRight } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const Shop = () => {
  const [params] = useSearchParams();
  const gender = params.get('gender');
  const brand = params.get('brand');
  const query = params.get('q');

  const { data: allProducts, error, isLoading } = useSWR('publicProducts', listProducts);

  const [selectedBrand, setSelectedBrand] = useState(brand || 'All');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState('all');

  useEffect(() => {
    setSelectedBrand(brand || 'All');
  }, [brand]);

  const productsToShow = useMemo(() => {
    return allProducts || [];
  }, [allProducts]);

  const isQuotaExceeded = error?.code === 'resource-exhausted' || error?.message?.toLowerCase().includes('quota');

  const brandCounts = useMemo(() => {
    let items = productsToShow;
    if (gender) items = items.filter(p => p.gender === gender);
    if (query) items = items.filter(p => p.title.toLowerCase().includes(query.toLowerCase()));
    
    const counts = {};
    items.forEach(p => {
      if (p.brand) {
        counts[p.brand] = (counts[p.brand] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [productsToShow, gender, query]);

  const filtered = useMemo(() => {
    let items = productsToShow;
    if (gender) items = items.filter(p => p.gender === gender);
    if (selectedBrand !== 'All') items = items.filter(p => p.brand === selectedBrand);
    if (query) items = items.filter(p => p.title.toLowerCase().includes(query.toLowerCase()));
    if (priceRange === 'low') items = items.filter(p => p.sale < 10000);
    if (priceRange === 'mid') items = items.filter(p => p.sale >= 10000 && p.sale < 30000);
    if (priceRange === 'high') items = items.filter(p => p.sale >= 30000);
    if (sortBy === 'low-high') items = [...items].sort((a, b) => a.sale - b.sale);
    if (sortBy === 'high-low') items = [...items].sort((a, b) => b.sale - a.sale);
    if (sortBy === 'discount') items = [...items].sort((a, b) => b.discount - a.discount);
    return items;
  }, [productsToShow, gender, selectedBrand, query, priceRange, sortBy]);

  const title = gender === 'men' ? 'Watches For Him' : gender === 'women' ? 'Watches For Her' : query ? `Search: ${query}` : 'All Watches';

  return (
    <div className="bg-white">
      {/* Page hero */}
      <div className="bg-neutral-100 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          {isQuotaExceeded && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg mb-6 flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <p className="text-xs font-medium">Daily request limit reached. Products might be temporarily unavailable.</p>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
            <Link to="/" className="hover:text-[#b8892d]">Home</Link>
            <ChevronRight size={12}/>
            <span className="text-neutral-800">{title}</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#1a1a1a]">{title}</h1>
          <p className="text-neutral-600 mt-1 text-sm">{filtered.length} products found</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="md:col-span-1">
          <div className="sticky top-32 space-y-6">
            <div>
              <h3 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2 uppercase tracking-wider text-xs"><SlidersHorizontal size={14}/> Brand</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => setSelectedBrand('All')} className={`text-sm hover:text-[#b8892d] transition-colors ${selectedBrand === 'All' ? 'text-[#b8892d] font-medium' : 'text-neutral-600'}`}>All Brands</button>
                </li>
                {brandCounts.map(b => (
                  <li key={b.name}>
                    <button onClick={() => setSelectedBrand(b.name)} className={`text-sm hover:text-[#b8892d] transition-colors ${selectedBrand === b.name ? 'text-[#b8892d] font-medium' : 'text-neutral-600'}`}>
                      {b.name} <span className="text-neutral-400">({b.count})</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-neutral-800 mb-3 uppercase tracking-wider text-xs">Price</h3>
              <ul className="space-y-2 text-sm">
                {[{v:'all',l:'All Prices'},{v:'low',l:'Under ₹10,000'},{v:'mid',l:'₹10,000 - ₹30,000'},{v:'high',l:'Above ₹30,000'}].map(p => (
                  <li key={p.v}>
                    <button onClick={() => setPriceRange(p.v)} className={`hover:text-[#b8892d] ${priceRange === p.v ? 'text-[#b8892d] font-medium' : 'text-neutral-600'}`}>{p.l}</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* Products */}
        <div className="md:col-span-3">

          <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-6">
            <div className="text-sm text-neutral-600">Showing {filtered.length} results</div>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border border-neutral-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#b8892d]">
              <option value="featured">Featured</option>
              <option value="low-high">Price: Low to High</option>
              <option value="high-low">Price: High to Low</option>
              <option value="discount">Best Discount</option>
            </select>
          </div>
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6">
              {[1,2,3,4,5,6].map(i => <div key={i}><Skeleton className="h-64 w-full mb-2"/><Skeleton className="h-4 w-3/4 mb-1"/><Skeleton className="h-4 w-1/4"/></div>)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-neutral-500">
              <p>No products found. Try adjusting your filters.</p>
              <Button onClick={() => { setSelectedBrand('All'); setPriceRange('all'); }} className="mt-4 bg-[#b8892d] hover:bg-[#a07826]">Reset Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6">
              {filtered.map(p => <ProductCard key={p.id} product={p}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
